import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bot,
  BriefcaseBusiness,
  ChevronDown,
  Megaphone,
  Plus,
  PlusIcon,
  Sparkles,
} from "lucide-react";
import { hasProductAccessWithFallback } from "@/utils/productAccess";
import { useProfile } from "@/context/ProfileContext";
import { useIntelligenceAccess } from "@/hooks/use-intelligence-access";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSidebar } from "@/components/ui/sidebar";
import { CreateAdModal } from "@/components/CreateAdModal/index";
import { cn } from "@/lib/utils";

/**
 * Primary “create” CTA under the team switcher: shortcuts to recruit (Graftus AI)
 * and/or leads ad creation based on product entitlements.
 */
export function SidebarCreateAdsCta() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { businessProfile, memberAccessLevel } = useProfile();
  const { state } = useSidebar();
  const sidebarCollapsed = state === "collapsed";
  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const hasIntelligence = useIntelligenceAccess();

  if (!businessProfile || memberAccessLevel === "none") {
    return null;
  }

  const now = Date.now();
  const hasRecruit = hasProductAccessWithFallback(
    businessProfile,
    "recruit",
    now,
  );
  const hasLeads = hasProductAccessWithFallback(businessProfile, "leads", now);

  if (!hasRecruit && !hasLeads && !hasIntelligence) {
    return null;
  }

  const buttonClass = cn(
    "w-full gap-2 bg-primary text-primary-foreground shadow-sm hover:bg-primary/90",
    "group-data-[collapsible=icon]:px-0",
  );

  const iconClass = "size-4 shrink-0";

  if (hasRecruit && hasLeads) {
    return (
      <>
        <div className="px-2 pb-2 pt-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="default"
                className={buttonClass}
                title={sidebarCollapsed ? t("createAd.ad") : undefined}
              >
                <PlusIcon className={iconClass} />
                <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
                  {t("createAd.new")}
                </span>
                <ChevronDown className="size-4 shrink-0 opacity-80 group-data-[collapsible=icon]:hidden" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start" sideOffset={6}>
              {hasIntelligence && (
                <DropdownMenuItem
                  className="gap-2"
                  onSelect={() => navigate("/coach")}
                >
                  <Bot className="size-4 text-primary" />
                  <span>{t("createAd.coachChat")}</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="gap-2"
                onSelect={() => setRecruitModalOpen(true)}
              >
                <BriefcaseBusiness className="size-4 text-primary" />
                <span>{t("createAd.recruitAd")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-2"
                onSelect={() => navigate("/leads/create")}
              >
                <Megaphone className="size-4 text-primary" />
                <span>{t("createAd.leadsAd")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <CreateAdModal
          isOpen={recruitModalOpen}
          onOpenChange={setRecruitModalOpen}
        />
      </>
    );
  }

  if (hasRecruit) {
    if (hasIntelligence) {
      return (
        <>
          <div className="px-2 pb-2 pt-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="default"
                  className={buttonClass}
                  title={sidebarCollapsed ? t("createAd.ad") : undefined}
                >
                  <PlusIcon className={iconClass} />
                  <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
                    {t("createAd.new")}
                  </span>
                  <ChevronDown className="size-4 shrink-0 opacity-80 group-data-[collapsible=icon]:hidden" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-56"
                align="start"
                sideOffset={6}
              >
                <DropdownMenuItem
                  className="gap-2"
                  onSelect={() => setRecruitModalOpen(true)}
                >
                  <BriefcaseBusiness className="size-4 text-primary" />
                  <span>{t("createAd.recruitAd")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-2"
                  onSelect={() => navigate("/coach")}
                >
                  <Bot className="size-4 text-primary" />
                  <span>{t("createAd.coachChat")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CreateAdModal
            isOpen={recruitModalOpen}
            onOpenChange={setRecruitModalOpen}
          />
        </>
      );
    }
    return (
      <>
        <div className="px-2 pb-2 pt-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="default"
                className={buttonClass}
                onClick={() => setRecruitModalOpen(true)}
              >
                <Sparkles className={iconClass} />
                <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
                  {t("createAd.jobAd")}
                </span>
              </Button>
            </TooltipTrigger>
            {sidebarCollapsed ? (
              <TooltipContent side="right">
                {t("createAd.jobAd")}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </div>
        <CreateAdModal
          isOpen={recruitModalOpen}
          onOpenChange={setRecruitModalOpen}
        />
      </>
    );
  }

  if (hasLeads) {
    if (hasIntelligence) {
      return (
        <div className="px-2 pb-2 pt-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="default"
                className={buttonClass}
                title={sidebarCollapsed ? t("createAd.ad") : undefined}
              >
                <PlusIcon className={iconClass} />
                <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
                  {t("createAd.new")}
                </span>
                <ChevronDown className="size-4 shrink-0 opacity-80 group-data-[collapsible=icon]:hidden" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start" sideOffset={6}>
              <DropdownMenuItem
                className="gap-2"
                onSelect={() => navigate("/leads/create")}
              >
                <Megaphone className="size-4 text-primary" />
                <span>{t("createAd.leadsAd")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="gap-2"
                onSelect={() => navigate("/coach")}
              >
                <Bot className="size-4 text-primary" />
                <span>{t("createAd.coachChat")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    }
    return (
      <div className="px-2 pb-2 pt-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="default"
              className={buttonClass}
              onClick={() => navigate("/leads/create")}
            >
              <Plus className={iconClass} />
              <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
                {t("createAd.leadsAdCta")}
              </span>
            </Button>
          </TooltipTrigger>
          {sidebarCollapsed ? (
            <TooltipContent side="right">
              {t("createAd.leadsAdCta")}
            </TooltipContent>
          ) : null}
        </Tooltip>
      </div>
    );
  }

  // Intelligence-only: no recruit or leads products
  return (
    <div className="px-2 pb-2 pt-0">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="default"
            className={buttonClass}
            onClick={() => navigate("/coach")}
          >
            <Bot className={iconClass} />
            <span className="min-w-0 flex-1 truncate text-left group-data-[collapsible=icon]:sr-only">
              {t("createAd.coachChat")}
            </span>
          </Button>
        </TooltipTrigger>
        {sidebarCollapsed ? (
          <TooltipContent side="right">
            {t("createAd.coachChat")}
          </TooltipContent>
        ) : null}
      </Tooltip>
    </div>
  );
}
